import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getTimeEntries, createTimeEntry, updateTimeEntry, deleteTimeEntry, approveTimeEntry, rejectTimeEntry, getProjects, getTasks, getEmployees } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { StatusBadge } from '../components/app';
import {
  Plus,
  Play,
  Pause,
  Stop,
  Clock,
  Check,
  X,
  Trash,
  CalendarBlank,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export function TimeTrackingPage() {
  const { t } = useLanguage();
  const { user, isManager } = useAuth();
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Timer state
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerProject, setTimerProject] = useState('');
  const [timerTask, setTimerTask] = useState('');
  const [timerDescription, setTimerDescription] = useState('');
  const timerRef = useRef(null);

  const [formData, setFormData] = useState({
    project_id: '',
    task_id: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    hours: 0,
    is_billable: true,
    is_overtime: false,
    billing_category: 'billable_development',
  });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [entriesRes, projectsRes, tasksRes, employeesRes] = await Promise.all([
        getTimeEntries(params),
        getProjects().catch(() => ({ data: [] })),
        getTasks().catch(() => ({ data: [] })),
        isManager ? getEmployees().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setEntries(entriesRes.data);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [isManager, statusFilter, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (isTimerRunning) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isTimerRunning]);


  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    setIsTimerRunning(true);
  };

  const pauseTimer = () => {
    setIsTimerRunning(false);
  };

  const stopTimer = async () => {
    setIsTimerRunning(false);
    const hours = timerSeconds / 3600;
    
    if (hours > 0 && timerDescription) {
      try {
        // Find employee for current user
        const employeeRes = await getEmployees({ user_id: user._id }).catch(() => ({ data: [] }));
        const employee = employeeRes.data.find(e => e.user_id === user._id) || employeeRes.data[0];
        
        await createTimeEntry({
          employee_id: employee?._id || user._id,
          project_id: timerProject || null,
          task_id: timerTask || null,
          description: timerDescription,
          date: new Date().toISOString().split('T')[0],
          hours: parseFloat(hours.toFixed(2)),
          is_billable: true,
          is_overtime: false,
        });
        toast.success(t('successSaved'));
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('errorOccurred'));
      }
    }
    
    setTimerSeconds(0);
    setTimerProject('');
    setTimerTask('');
    setTimerDescription('');
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    try {
      const employeeRes = await getEmployees().catch(() => ({ data: [] }));
      const employee = employeeRes.data.find(e => e.user_id === user._id) || employeeRes.data[0];
      
      await createTimeEntry({
        ...formData,
        employee_id: employee?._id || user._id,
        project_id: formData.project_id || null,
        task_id: formData.task_id || null,
      });
      toast.success(t('successSaved'));
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveTimeEntry(id);
      toast.success(t('successApproved'));
      fetchData();
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Reason for rejection:');
    if (reason !== null) {
      try {
        await rejectTimeEntry(id, reason);
        toast.success(t('successRejected'));
        fetchData();
      } catch (error) {
        toast.error(t('errorOccurred'));
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await deleteTimeEntry(id);
        toast.success(t('successDeleted'));
        fetchData();
      } catch (error) {
        toast.error(t('errorOccurred'));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      task_id: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      start_time: '',
      end_time: '',
      hours: 0,
      is_billable: true,
      is_overtime: false,
      billing_category: 'billable_development',
    });
  };

  const billingCategories = [
    { value: 'billable_development', label: 'Desarrollo Facturable', billable: true },
    { value: 'billable_meeting', label: 'Reuniones Facturables', billable: true },
    { value: 'billable_support', label: 'Soporte Facturable', billable: true },
    { value: 'non_billable_internal', label: 'Reuniones Internas', billable: false },
    { value: 'non_billable_training', label: 'Capacitación', billable: false },
    { value: 'non_billable_admin', label: 'Administrativo', billable: false },
    { value: 'non_billable_rework', label: 'Retrabajo', billable: false },
    { value: 'warranty_support', label: 'Soporte Garantía', billable: false },
  ];

  const getProjectName = (projectId) => {
    const project = projects.find(p => p._id === projectId);
    return project?.name || '-';
  };

  const getTaskName = (taskId) => {
    const task = tasks.find(t => t._id === taskId);
    return task?.title || '-';
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e._id === employeeId);
    return employee ? `${employee.name} ${employee.last_name}` : '-';
  };

  const groupedEntries = entries.reduce((acc, entry) => {
    const date = entry.date || 'No date';
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);

  return (
    <Layout title={t('timeTracking')}>
      <div className="space-y-6" data-testid="time-tracking-page">
        {/* Timer Section */}
        <div className="valhalla-card p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Timer Display */}
            <div className={`flex-shrink-0 flex flex-col items-center justify-center p-6 rounded-xl bg-[#0B0E14] border border-[#242E42] ${isTimerRunning ? 'timer-active border-[#E2B05E]' : ''}`}>
              <p className="font-['Outfit'] text-5xl font-bold text-[#E2B05E] tracking-wider" data-testid="timer-display">
                {formatTime(timerSeconds)}
              </p>
              <div className="flex gap-2 mt-4">
                {!isTimerRunning ? (
                  <Button
                    onClick={startTimer}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="start-timer-btn"
                  >
                    <Play weight="fill" className="w-4 h-4 mr-2" />
                    {t('startTimer')}
                  </Button>
                ) : (
                  <Button
                    onClick={pauseTimer}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="pause-timer-btn"
                  >
                    <Pause weight="fill" className="w-4 h-4 mr-2" />
                    {t('pauseTimer')}
                  </Button>
                )}
                <Button
                  onClick={stopTimer}
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                  disabled={timerSeconds === 0}
                  data-testid="stop-timer-btn"
                >
                  <Stop weight="fill" className="w-4 h-4 mr-2" />
                  {t('stopTimer')}
                </Button>
              </div>
            </div>

            {/* Timer Form */}
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">Project</Label>
                  <Select value={timerProject || "none"} onValueChange={(v) => setTimerProject(v === "none" ? "" : v)}>
                    <SelectTrigger className="valhalla-input">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151B26] border-[#242E42]">
                      <SelectItem value="none">No project</SelectItem>
                      {projects.map(proj => (
                        <SelectItem key={proj._id} value={proj._id}>{proj.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">Task</Label>
                  <Select value={timerTask || "none"} onValueChange={(v) => setTimerTask(v === "none" ? "" : v)}>
                    <SelectTrigger className="valhalla-input">
                      <SelectValue placeholder="Select task" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151B26] border-[#242E42]">
                      <SelectItem value="none">No task</SelectItem>
                      {tasks.filter(t => !timerProject || t.project_id === timerProject).map(task => (
                        <SelectItem key={task._id} value={task._id}>{task.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">{t('description')}</Label>
                <Textarea
                  value={timerDescription}
                  onChange={(e) => setTimerDescription(e.target.value)}
                  className="valhalla-input min-h-[80px]"
                  placeholder="What are you working on?"
                  data-testid="timer-description"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 valhalla-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="pendiente">{t('pending')}</SelectItem>
                <SelectItem value="aprobada">{t('approved')}</SelectItem>
                <SelectItem value="rechazada">{t('rejected')}</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-[#9CA3AF]">
              Total: <span className="text-[#E2B05E] font-bold">{totalHours.toFixed(2)}h</span>
            </div>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold" data-testid="manual-entry-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('manualEntry')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#151B26] border-[#242E42] max-w-md">
              <DialogHeader>
                <DialogTitle className="text-[#F3F4F6] font-['Outfit']">
                  {t('manualEntry')}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleManualEntry} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">Project</Label>
                  <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({...formData, project_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="valhalla-input">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151B26] border-[#242E42]">
                      <SelectItem value="none">No project</SelectItem>
                      {projects.map(proj => (
                        <SelectItem key={proj._id} value={proj._id}>{proj.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{t('description')}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="valhalla-input"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('date')}</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="valhalla-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('hours')}</Label>
                    <Input
                      type="number"
                      step="0.25"
                      value={formData.hours}
                      onChange={(e) => setFormData({...formData, hours: parseFloat(e.target.value) || 0})}
                      className="valhalla-input"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="billable"
                      checked={formData.is_billable}
                      onCheckedChange={(checked) => setFormData({...formData, is_billable: checked})}
                    />
                    <Label htmlFor="billable" className="text-[#9CA3AF] cursor-pointer">{t('billable')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="overtime"
                      checked={formData.is_overtime}
                      onCheckedChange={(checked) => setFormData({...formData, is_overtime: checked})}
                    />
                    <Label htmlFor="overtime" className="text-[#9CA3AF] cursor-pointer">{t('overtime')}</Label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-[#242E42]">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#242E42] text-[#9CA3AF]">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14]">
                    {t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Time Entries by Date */}
        <div className="space-y-6">
          {Object.entries(groupedEntries).sort((a, b) => b[0].localeCompare(a[0])).map(([date, dateEntries]) => (
            <div key={date} className="space-y-3">
              <div className="flex items-center gap-3">
                <CalendarBlank className="w-4 h-4 text-[#9CA3AF]" />
                <h3 className="font-medium text-[#F3F4F6]">{date}</h3>
                <span className="text-sm text-[#9CA3AF]">
                  {dateEntries.reduce((sum, e) => sum + (e.hours || 0), 0).toFixed(2)}h
                </span>
              </div>
              
              <div className="space-y-2">
                {dateEntries.map((entry) => (
                  <div key={entry._id} className="valhalla-card p-4 flex items-center justify-between" data-testid={`time-entry-${entry._id}`}>
                    <div className="flex-1">
                      <p className="text-[#F3F4F6] mb-1">{entry.description}</p>
                      <div className="flex items-center gap-4 text-xs text-[#9CA3AF]">
                        <span>{getProjectName(entry.project_id)}</span>
                        <span>{getTaskName(entry.task_id)}</span>
                        {isManager && <span>{getEmployeeName(entry.employee_id)}</span>}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        {entry.is_billable && (
                          <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                            {t('billable')}
                          </Badge>
                        )}
                        {entry.is_overtime && (
                          <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                            {t('overtime')}
                          </Badge>
                        )}
                        <StatusBadge value={entry.status} />
                      </div>
                      
                      <div className="flex items-center gap-1 text-[#E2B05E] font-bold">
                        <Clock className="w-4 h-4" />
                        <span>{entry.hours?.toFixed(2)}h</span>
                      </div>
                      
                      <div className="flex gap-1">
                        {entry.status === 'pendiente' && isManager && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => handleApprove(entry._id)} className="text-emerald-400 hover:bg-emerald-500/10">
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleReject(entry._id)} className="text-red-400 hover:bg-red-500/10">
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(entry._id)} className="text-[#9CA3AF] hover:text-red-400">
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {entries.length === 0 && !loading && (
          <div className="valhalla-card p-12 text-center">
            <Clock className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">{t('noData')}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TimeTrackingPage;
